import { Component, computed, inject, Injector, linkedSignal, signal } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { form, FormField, FormRoot, required, validateStandardSchema } from '@angular/forms/signals'
import { MatButtonModule } from '@angular/material/button'
import { MatNativeDateModule } from '@angular/material/core'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatDialog } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatRadioModule } from '@angular/material/radio'
import { EMAIL_CHANGE_LINK_HOURS, Gender, ProfileField, requiredProfileFields } from '@seiri/shared/common'
import { AuthApiService, UsersApiService } from '../../api'
import { FieldErrorDirective } from '../../directives/field-error.directive'
import { ProfileFormModel, profileFormModel, toEditUserBody, toProfileFormModel } from '../../forms/profile-form.model'
import { LoggerService } from '../../logging'
import { SaveBarComponent } from '../save-bar/save-bar.component'
import { ACCOUNT_HOST } from './account-host'
import { ChangeEmailDialogComponent } from './change-email-dialog.component'
import { ChangePasswordDialogComponent } from './change-password-dialog.component'

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/**
 * Môj profil: the signed-in person's own account (docs/account.md). A bare page body - the app's shell, heading level
 * aside, stays the host's - so every zone mounts the same screen; the host provides `ACCOUNT_HOST`.
 *
 * Osobné údaje is one form with the save bar. Prihlásenie a zabezpečenie lists the sign-in e-mail and the password
 * as rows of the same shape - what it is and its value, then the one action - each changed through its own dialog. A
 * host may project a note above those rows (`[accountEmailHint]`), such as the owner's hint about where the premise's
 * own contact e-mail is set.
 */
@Component({
  selector: 'seiri-account-profile',
  imports: [
    FieldErrorDirective,
    FormField,
    FormRoot,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatRadioModule,
    SaveBarComponent,
  ],
  templateUrl: './account-profile.component.html',
  styleUrl: './account-profile.component.css',
})
export class AccountProfileComponent {
  private readonly host = inject(ACCOUNT_HOST)
  private readonly usersApi = inject(UsersApiService)
  private readonly authApi = inject(AuthApiService)
  private readonly logger = inject(LoggerService).forContext('AccountProfile')
  private readonly dialog = inject(MatDialog)
  private readonly injector = inject(Injector)

  protected readonly Gender = Gender
  protected readonly user = this.host.user
  protected readonly email = computed(() => this.user()?.email ?? '')
  protected readonly pendingEmail = computed(() => this.user()?.pendingEmail ?? null)
  protected readonly linkHours = EMAIL_CHANGE_LINK_HOURS
  protected readonly cancelling = signal(false)
  private readonly required = computed(() => requiredProfileFields({ entityOwner: !!this.user()?.entityOwner }))
  private readonly isRequired = (field: ProfileField) => this.required().includes(field)
  private readonly schema = computed(() => profileFormModel(this.required()))

  /** What was last saved; follows the session's user, so a save elsewhere (another tab) resets the form. */
  private readonly reference = computed(() => toProfileFormModel(this.user()))
  protected readonly model = linkedSignal<ProfileFormModel>(() => structuredClone(this.reference()))
  protected readonly changed = computed(() => !sameValue(this.model(), this.reference()))

  protected readonly form = form(
    this.model,
    (path) => {
      validateStandardSchema(path, () => this.schema())
      // Only marks the fields required - Material's asterisk, `aria-required`; the schema above says why it fails.
      required(path.firstName, { when: () => this.isRequired('firstName') })
      required(path.lastName, { when: () => this.isRequired('lastName') })
      required(path.birthDate, { when: () => this.isRequired('birthDate') })
    },
    {
      submission: {
        action: async (field) => {
          const user = this.user()
          if (!user) return undefined
          try {
            this.host.setUser(await this.usersApi.editUser(user.id, toEditUserBody(field().value())))
            this.host.notify('success', 'Osobné údaje boli uložené')
          } catch (error) {
            this.logger.error('profile save failed', error)
            this.host.notify('error', 'Osobné údaje sa nepodarilo uložiť', error)
          }
          return undefined
        },
      },
    },
  )

  // The dialogs need the host's ACCOUNT_HOST, which MatDialog's root injector does not see.
  private readonly dialogConfig = { width: '80%', maxWidth: '480px', autoFocus: 'input', injector: this.injector }

  protected async changePassword() {
    const ref = this.dialog.open<ChangePasswordDialogComponent, void, boolean>(
      ChangePasswordDialogComponent,
      this.dialogConfig,
    )
    if (await firstValueFrom(ref.afterClosed())) {
      this.host.notify('success', 'Heslo bolo zmenené')
    }
  }

  protected changeEmail() {
    this.dialog.open(ChangeEmailDialogComponent, this.dialogConfig)
  }

  protected async cancelEmailChange() {
    this.cancelling.set(true)
    try {
      await this.authApi.cancelEmailChange()
      this.host.setUser(await this.usersApi.me())
      this.host.notify('success', 'Zmena e-mailu bola zrušená')
    } catch (error) {
      this.logger.error('email change cancel failed', error)
      this.host.notify('error', 'Zmenu e-mailu sa nepodarilo zrušiť', error)
    } finally {
      this.cancelling.set(false)
    }
  }

  protected discard() {
    this.form().reset(structuredClone(this.reference()))
  }

  protected problem(errors: readonly { message?: string }[]): string {
    return [...new Set(errors.map((error) => error.message).filter((message): message is string => !!message))].join(
      ' ',
    )
  }
}
